/**
 * +atmos: light shafts (god rays) at a few vista points: soft additive beams slanting down along
 * the sunlight, billboarded around their own axis, gently breathing. One instanced draw; high
 * quality only. They fade when the camera is very close (no screen-filling haze) or far away.
 */
import * as THREE from 'three';
import { U } from '../../style';
import { ATMOS } from './uniforms';

export interface ShaftSpot {
  /** Sim position of the vista and its ground height. */
  x: number;
  y: number;
  h: number;
  /** Scatter radius (yd) and count. */
  r: number;
  n: number;
}

/** Mossvale's vista points (content/zones/mossvaleTerrain.ts): the Pale Rise summit, Silverstep Falls, Bramblewood's edge. */
export const MOSSVALE_SHAFT_SPOTS: readonly Omit<ShaftSpot, 'h'>[] = [
  { x: 440, y: 650, r: 24, n: 6 },
  { x: 704, y: 512, r: 14, n: 5 },
  { x: 262, y: 520, r: 26, n: 6 },
];

export function buildShafts(spots: readonly ShaftSpot[]): { mesh: THREE.Mesh; uStrength: { value: number }; dispose(): void } {
  let seed = 4242;
  const rnd = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const inst: number[] = [];
  const size: number[] = [];
  for (const s of spots)
    for (let i = 0; i < s.n; i++) {
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd()) * s.r;
      inst.push(s.x + Math.cos(a) * d, s.h - 1, -(s.y + Math.sin(a) * d), rnd() * 100);
      size.push(2.5 + rnd() * 4.5, 50 + rnd() * 35);
    }
  const quad = new THREE.PlaneGeometry(2, 1, 1, 1).translate(0, 0.5, 0);
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = quad.index;
  geo.setAttribute('position', quad.attributes['position']!);
  geo.setAttribute('aBase', new THREE.InstancedBufferAttribute(new Float32Array(inst), 4));
  geo.setAttribute('aSize', new THREE.InstancedBufferAttribute(new Float32Array(size), 2));
  geo.instanceCount = inst.length / 4;
  const uStrength = { value: 0 };
  const mat = new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uSun: ATMOS.uSunDir, uSunCol: ATMOS.uSunColor, uStrength },
    vertexShader: `attribute vec4 aBase; attribute vec2 aSize; uniform float uTime; uniform vec3 uSun;
      varying vec2 vUv; varying float vFade; varying float vSeed;
      void main(){
        vec3 axis = normalize(uSun + vec3(0.0, 0.35, 0.0));
        vec3 mid = aBase.xyz + axis * aSize.y * 0.5;
        vec3 toCam = cameraPosition - mid;
        vec3 side = normalize(cross(axis, toCam));
        float taper = mix(1.0, 1.9, position.y);
        vec3 wp = aBase.xyz + axis * position.y * aSize.y + side * position.x * aSize.x * taper;
        float dc = length(cameraPosition - aBase.xyz);
        vFade = smoothstep(12.0, 40.0, dc) * (1.0 - smoothstep(260.0, 420.0, dc));
        vUv = vec2(position.x, position.y); vSeed = aBase.w;
        gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
      }`,
    fragmentShader: `uniform float uTime; uniform vec3 uSunCol; uniform float uStrength;
      varying vec2 vUv; varying float vFade; varying float vSeed;
      void main(){
        float x = 1.0 - vUv.x * vUv.x;
        float along = smoothstep(0.0, 0.18, vUv.y) * (1.0 - smoothstep(0.45, 1.0, vUv.y));
        float breathe = 0.65 + 0.35 * sin(uTime * 0.35 + vSeed) * sin(uTime * 0.21 + vSeed * 1.7);
        float a = x * x * along * breathe * vFade * uStrength * 0.34;
        if (a < 0.002) discard;
        gl_FragColor = vec4(uSunCol * a, 1.0); // additive: SRC_ALPHA, ONE
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  mat.name = 'light-shafts';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'light-shafts';
  mesh.frustumCulled = false;
  mesh.renderOrder = 5;
  return {
    mesh,
    uStrength,
    dispose() {
      mesh.removeFromParent();
      quad.dispose();
      geo.dispose();
      mat.dispose();
    },
  };
}
